import * as React from 'react';

import { getLocale } from '@rematter/common/locale';
import { Unit } from '@rematter/frontend-lib-api-client';
import { humanReadablePercent } from '@rematter/ui-web';

import { ValidationStatusCell } from './fields';
import { BrokeredOrderLineItemUpdate } from './types';

import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { BrokeredOrderValidationError } from '~/modules/brokerage/types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '~/modules/brokerage/types/brokeredOrderLineItem';
import {
  ComputedPriceCell,
  DeleteButtonCell,
  HeaderCell,
  InventoryItemSelect,
  LineItemHeader,
  LineItemRow,
  LineItemTable,
  QuantityFieldCell,
  UnitFieldCell,
} from '~/shared/components/line-items';

const BROKERED_ORDER_LINE_ITEM_GRID =
  'grid-cols-[minmax(16rem,_2fr)_100px_100px_120px_120px_120px_120px_140px_var(--size-input)_var(--size-input)]';

interface OrderLineItemRowProps {
  lineItem: BrokeredOrderLineItemRecord;
  disabled: boolean;
  errors?: BrokeredOrderValidationError[];
  validationRan: boolean;
  onUpdateLineItem: (id: string, updates: BrokeredOrderLineItemUpdate) => void;
  onDeleteLineItem: (id: string) => void;
}

const OrderLineItemRow = React.memo<OrderLineItemRowProps>(
  ({
    lineItem,
    disabled,
    errors,
    validationRan,
    onUpdateLineItem,
    onDeleteLineItem,
  }) => {
    const handleMaterialChange = React.useCallback(
      (itemId: string | undefined) =>
        onUpdateLineItem(lineItem.id, { itemId: itemId ?? undefined }),
      [lineItem.id, onUpdateLineItem],
    );

    const handleUnitChange = React.useCallback(
      (unit?: Unit) => {
        if (unit) onUpdateLineItem(lineItem.id, { unit });
      },
      [lineItem.id, onUpdateLineItem],
    );

    const handleQuantityChange = React.useCallback(
      (quantity: number) => onUpdateLineItem(lineItem.id, { quantity }),
      [lineItem.id, onUpdateLineItem],
    );

    const handleDelete = React.useCallback(
      () => onDeleteLineItem(lineItem.id),
      [lineItem.id, onDeleteLineItem],
    );

    return (
      <LineItemRow gridTemplate={BROKERED_ORDER_LINE_ITEM_GRID}>
        <InventoryItemSelect
          value={lineItem.itemId}
          onChange={handleMaterialChange}
          disabled={disabled}
        />
        <UnitFieldCell
          value={lineItem.unit}
          onChange={handleUnitChange}
          disabled={disabled}
        />
        <QuantityFieldCell
          value={lineItem.quantity}
          pricingUnit={lineItem.unit}
          disabled={disabled}
          onChange={handleQuantityChange}
        />
        <ComputedPriceCell value={lineItem.purchaseUnitPrice} />
        <ComputedPriceCell value={lineItem.salesUnitPrice} />
        <ComputedPriceCell value={lineItem.purchaseTotal} />
        <ComputedPriceCell value={lineItem.salesTotal} />
        <ComputedPriceCell
          value={lineItem.margin}
          subtitle={humanReadablePercent(lineItem.marginPercent ?? 0, {
            locale: getLocale(),
          })}
          showNegative
        />
        <ValidationStatusCell errors={errors} validationRan={validationRan} />
        <DeleteButtonCell onDelete={handleDelete} disabled={disabled} />
      </LineItemRow>
    );
  },
);

interface BrokeredOrderLineItemsTableProps {
  lineItems: BrokeredOrderLineItemRecord[];
  disabled?: boolean;
  validationErrorsByItemId?: Map<string, BrokeredOrderValidationError[]> | null;
  validationRan?: boolean;
  onUpdateLineItem: (id: string, updates: BrokeredOrderLineItemUpdate) => void;
  onDeleteLineItem: (id: string) => void;
  onAddLineItem: () => void;
}

export const BrokeredOrderLineItemsTable: React.FC<
  BrokeredOrderLineItemsTableProps
> = ({
  lineItems,
  disabled = false,
  validationErrorsByItemId,
  validationRan = false,
  onUpdateLineItem,
  onDeleteLineItem,
  onAddLineItem,
}) => {
  const t = useTranslations();
  const isEmpty = lineItems.length === 0;

  return (
    <LineItemTable
      isEmpty={isEmpty}
      onAdd={onAddLineItem}
      hideAddButton={disabled}
      disabled={disabled}
      addButtonLabel={t.orders.materials.addButton}
      emptyTitle={t.orders.materials.empty}
      emptyDescription={t.orders.materials.emptyDescription}
      className="@BrokeredOrderLineItemsTable"
    >
      <LineItemHeader gridTemplate={BROKERED_ORDER_LINE_ITEM_GRID}>
        <HeaderCell>{t.orders.materials.columns.material}</HeaderCell>
        <HeaderCell>{t.orders.materials.fields.unit}</HeaderCell>
        <HeaderCell>{t.orders.materials.fields.quantity}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.purchasePrice}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.salesPrice}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.buyTotal}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.sellTotal}</HeaderCell>
        <HeaderCell>{t.orders.materials.columns.margin}</HeaderCell>
        <HeaderCell />
        <HeaderCell />
      </LineItemHeader>

      <div className="flex flex-col gap-2">
        {lineItems.map((lineItem) => (
          <OrderLineItemRow
            key={lineItem.id}
            lineItem={lineItem}
            disabled={disabled}
            errors={validationErrorsByItemId?.get(lineItem.itemId)}
            validationRan={validationRan}
            onUpdateLineItem={onUpdateLineItem}
            onDeleteLineItem={onDeleteLineItem}
          />
        ))}
      </div>
    </LineItemTable>
  );
};
